/**
 * Org / RBAC store for the AppShell.
 *
 * The server is the source of truth: `bootstrap()` loads the active org, the
 * effective role, the flat capability list (`/api/me/capabilities`) and the
 * already-filtered nav tree (`/api/me/navigation`). The shell renders only what
 * the server returns. See docs/GRANULA_MASTER_PLAN.md §4 + §12.3.
 *
 * When VITE_ENABLE_ROLE_PREVIEW=true, a `?role=` query param (or the TopBar
 * "Preview as" select) flips the store into PREVIEW mode — capabilities are
 * derived client-side so you can see each tier's nav without switching accounts.
 * Preview is off by default; set VITE_ENABLE_ROLE_PREVIEW=true in .env.local to
 * re-enable it for a specific environment.
 */
import { create } from 'zustand'
import { api } from '@/lib/api'
import { queryClient } from '@/lib/queryClient'
import { qk } from '@/lib/queryKeys'
import { PRIMARY_NAV, type PrimaryItem, type SecondaryItem } from '@/components/nav/navConfig'
import type { Organization } from '@/types'

/** Set VITE_ENABLE_ROLE_PREVIEW=true to turn on the "Preview as" demo switcher. */
export const PREVIEW_ENABLED = import.meta.env.VITE_ENABLE_ROLE_PREVIEW === 'true'

export type OrgRole = 'program_manager' | 'project_manager' | 'team_lead' | 'member'

const OFFLINE_ORG_SESSION_KEY = 'granula.org.session'

type CachedOrgSession = {
  org: Organization | null
  role: OrgRole
  caps: string[]
  navKeys: string[]
  capMinRank: Record<string, number>
}

// Legacy values (pre-2026-07-06 3-tier system) — normalize on read.
const _LEGACY_ROLES: Record<string, OrgRole> = {
  owner: 'program_manager', super_admin: 'program_manager',
  admin: 'program_manager', project_lead: 'project_manager',
}
export function normalizeOrgRole(r: string): OrgRole {
  return (_LEGACY_ROLES[r] ?? (r as OrgRole)) in ROLE_RANK ? (_LEGACY_ROLES[r] ?? (r as OrgRole)) : 'member'
}

export const ROLE_RANK: Record<OrgRole, number> = {
  member: 1, team_lead: 2, project_manager: 3, program_manager: 4,
}

function readCachedOrgSession(): CachedOrgSession | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(OFFLINE_ORG_SESSION_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<CachedOrgSession>
    if (!parsed.org || typeof parsed.org.id !== 'number') return null
    return {
      org: parsed.org,
      role: normalizeOrgRole(parsed.role ?? 'member'),
      caps: Array.isArray(parsed.caps) ? parsed.caps.filter((cap): cap is string => typeof cap === 'string') : [],
      navKeys: Array.isArray(parsed.navKeys) ? parsed.navKeys.filter((key): key is string => typeof key === 'string') : [],
      capMinRank: parsed.capMinRank && typeof parsed.capMinRank === 'object' ? parsed.capMinRank : {},
    }
  } catch {
    return null
  }
}

function persistOrgSession(session: CachedOrgSession) {
  if (typeof window === 'undefined') return
  try {
    sessionStorage.setItem(OFFLINE_ORG_SESSION_KEY, JSON.stringify(session))
  } catch {
    // Storage can be unavailable in private browsing; online bootstrap still works.
  }
}

const initialOrgSession = readCachedOrgSession()

/** Display names for the 4-tier hierarchy (2026-06-29 owner review). */
export const ROLE_LABELS: Record<OrgRole, string> = {
  member: 'Member', team_lead: 'Team Lead',
  project_manager: 'Project Manager', program_manager: 'Program Manager',
}

/** Numeric authority of any (possibly legacy) role string. */
export function roleRank(r: string): number {
  return ROLE_RANK[normalizeOrgRole(r)]
}

/** API-UNREACHABLE DEV-PREVIEW FALLBACK ONLY — not a source of truth and not
 *  a mirror to maintain. Live mode and a reachable-API preview both derive
 *  `capMinRank` from GET /api/orgs/current/roles (see bootstrap() below); this
 *  constant is read only in the `catch` branch, when the API can't be reached
 *  at all (unauthenticated local dev). It previously drifted from
 *  app/core/rbac.py's CAPABILITY_MIN_RANK — missing employee_document.view/
 *  manage — for a full plan cycle before anyone noticed (plan 025 / A6). */
const CAP_MIN_RANK: Record<string, number> = {
  'task.create': 1, 'task.edit_own': 1, 'task.assign': 2, 'task.approve': 2,
  'project.view': 1, 'project.create': 4, 'project.archive': 4, 'project.delete': 4,
  'portfolio.view': 4,
  // project.manage — project_manager+ (rank 3); backend also grants it per-project
  // via ProjectMember.role='project_manager'.
  'project.manage': 3,
  // project.manage_statuses — team_lead+ (rank 2); backend also checks is_team_lead_of_project.
  // v1.1 custom roles at rank ≥ 2 inherit this automatically. See MASTER_PLAN §7.3.
  'project.manage_statuses': 2,
  'people.view': 1, 'people.manage': 4,
  'leave.request': 1, 'leave.approve': 2,
  'leave.policy.manage': 4, 'leave.balance.view': 2, 'leave.calendar.view': 2,
  'expense.submit': 1, 'requisition.submit': 1,
  'expense.approve': 2, 'finance.disburse': 2, 'finance.view_all': 4,
  'budget.view': 2, 'budget.manage': 4,
  'reports.team': 2, 'reports.org': 4, 'org.manage': 4, 'activity.view': 4,
  'ai.use': 99, // tier-gated; ships in M3
  'employee_document.view': 4, 'employee_document.manage': 4,
}

function previewRoleFromQuery(): OrgRole | null {
  if (!PREVIEW_ENABLED) return null
  if (typeof window === 'undefined') return null
  const raw = new URLSearchParams(window.location.search).get('role') ?? ''
  const normalized = normalizeOrgRole(raw)
  return normalized in ROLE_RANK ? normalized : null
}

/** Composite key for a secondary item, e.g. "projects.portfolio". */
const secKey = (parent: string, child: string) => `${parent}.${child}`

export const ORG_ROLES: OrgRole[] = ['member', 'team_lead', 'project_manager', 'program_manager']

interface OrgState {
  mode: 'loading' | 'server' | 'preview' | 'offline' | 'error'
  // Widened from {id, name} (design decision 9, plan 023 step 7) — GET /api/me
  // already ships the full org (timezone, working_days, date_format, working
  // hours); this store no longer throws that data away. Preference-shaped
  // fields (timezone/date_format/work_*_min) are still read through
  // usePreferences()/qk.identity(), not this store — see mutations.ts's
  // invalidatePreferences(), which keeps both copies in sync on every write.
  org: Organization | null
  role: OrgRole
  caps: Set<string>          // server-granted capabilities (live mode)
  navKeys: Set<string>       // server-visible nav keys: "projects", "projects.portfolio", …
  // Server-derived min-rank-per-capability (GET /api/orgs/current/roles),
  // used for ?role= PREVIEW after a successful bootstrap. Empty only when
  // bootstrap() itself failed (see the CAP_MIN_RANK fallback above) — never
  // read in live mode, see can() below.
  capMinRank: Record<string, number>
  bootstrap: () => Promise<void>
  setRole: (role: OrgRole) => void   // enters PREVIEW mode (no-op when PREVIEW_ENABLED is false)
  can: (cap?: string | null) => boolean
  visiblePrimary: () => PrimaryItem[]
  visibleSecondary: (item: PrimaryItem) => SecondaryItem[]
}

export const orgStore = create<OrgState>((set, get) => ({
  mode: initialOrgSession ? 'offline' : 'loading',
  org: initialOrgSession?.org ?? null,
  role: initialOrgSession?.role ?? previewRoleFromQuery() ?? 'member',
  caps: new Set(initialOrgSession?.caps ?? []),
  navKeys: new Set(initialOrgSession?.navKeys ?? []),
  capMinRank: initialOrgSession?.capMinRank ?? {},

  bootstrap: async () => {
    if (!navigator.onLine && get().org) {
      set({ mode: 'offline' })
      return
    }
    try {
      const [me, caps, nav, roles] = await Promise.all([
        api.get('/me'),
        api.get('/me/capabilities'),
        api.get('/me/navigation'),
        api.get('/orgs/current/roles'),
      ])
      const navKeys = new Set<string>()
      for (const p of nav.data.primary ?? []) {
        navKeys.add(p.key)
        for (const s of p.secondary ?? []) navKeys.add(secKey(p.key, s.key))
      }
      const capMinRank: Record<string, number> = {}
      for (const row of roles.data.capabilities ?? []) capMinRank[row.key] = row.min_rank
      const preview = previewRoleFromQuery()  // null when PREVIEW_ENABLED is false
      set({
        org: me.data.org ?? null,
        role: preview ?? normalizeOrgRole(me.data.role ?? 'member'),
        caps: new Set<string>(caps.data.capabilities ?? []),
        navKeys,
        capMinRank,
        mode: preview ? 'preview' : 'server',
      })
      persistOrgSession({
        org: me.data.org ?? null,
        role: preview ?? normalizeOrgRole(me.data.role ?? 'member'),
        caps: Array.from(caps.data.capabilities ?? []),
        navKeys: Array.from(navKeys),
        capMinRank,
      })
      // Seed the React Query cache under the same key usePreferences() reads
      // (qk.identity()) with the /api/me response this call already fetched,
      // so the first usePreferences()/useIdentityQuery() mount doesn't repeat
      // the request — plan 023 step 7, design decision 9. This is a SEED, not
      // a substitute for a real query: usePreferences() still owns its own
      // useQuery on this key so it re-renders after a preferences mutation
      // invalidates it, which a bare setQueryData with no observer would not.
      queryClient.setQueryData(qk.identity(), me.data)
      // Same idea for useOrgRoles() — this call already fetched the matrix,
      // so its first mount inside Settings shouldn't repeat the request.
      queryClient.setQueryData(qk.org.roles(), roles.data)
    } catch {
      if (get().org) {
        set({ mode: 'offline' })
      } else if (PREVIEW_ENABLED) {
        // Dev-only demo fallback: derive capabilities client-side so the shell
        // renders even when the API is unreachable (unauthenticated local dev).
        set({ mode: 'preview' })
      } else {
        // Production path: never grant capabilities from client-side defaults.
        // Show an empty, locked shell with a retry affordance.
        set({ mode: 'error', caps: new Set(), navKeys: new Set() })
      }
    }
  },

  setRole: (role) => {
    if (PREVIEW_ENABLED) set({ role, mode: 'preview' })
    // no-op when preview is disabled — role is always server-authoritative
  },

  can: (cap) => {
    if (!cap) return true
    const { mode, caps, role, capMinRank } = get()
    if (mode === 'server' || mode === 'offline') return caps.has(cap)
    if (mode === 'preview' && PREVIEW_ENABLED) {
      // Server-derived ranks when a bootstrap succeeded (the ?role= preview
      // case); the hardcoded table is only for the catch branch, where
      // bootstrap() never populated capMinRank at all — never in live mode.
      const table = Object.keys(capMinRank).length > 0 ? capMinRank : CAP_MIN_RANK
      const need = table[cap] ?? 99
      return ROLE_RANK[role] >= need
    }
    // loading / error / preview-when-flag-off → grant nothing
    return false
  },

  visiblePrimary: () => {
    const s = get()
    if (s.mode === 'server' || s.mode === 'offline') return PRIMARY_NAV.filter((i) => s.navKeys.has(i.key))
    if (s.mode === 'preview' && PREVIEW_ENABLED) return PRIMARY_NAV.filter((i) => s.can(i.cap))
    return []  // loading / error → show nothing until server responds
  },

  visibleSecondary: (item) => {
    const s = get()
    const all = item.secondary ?? []
    if (s.mode === 'server' || s.mode === 'offline') return all.filter((sec) => s.navKeys.has(secKey(item.key, sec.key)))
    if (s.mode === 'preview' && PREVIEW_ENABLED) return all.filter((sec) => s.can(sec.cap))
    return []
  },
}))
